import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import PersonnelSearch from '../components/PersonnelSearch';
import useFetch from 'use-http';
import LoadingSpinner from 'src/components/common/LoadingSpinner';
import DialogList from 'src/dialogs';
import actions from 'src/actions';
import PersonnelNavBar from '../components/PersonnelNavBar';
import PersonnelToast from '../components/PersonnelToast';
import TSDILandman from 'src/models/personnel/TSDILandman';
import { IRootState } from 'src/reducers';
import Button from 'src/components/controls/forms/redesign/FormButton';

import '../BenefitStyleSheet.scss';
import BenefitRate from 'src/models/personnel/BenefitRate';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import EditBenefitRateDialog from '../dialogs/benefits/EditBenefitRateDialog';
import PersonnelAdvancedQuickFind from '../dialogs/PersonnelAdvancedQuickFindDialog';

const BenefitRatesPage = () => {
    const { register, setValue, getValues } = useForm();
    const { get, post, response } = useFetch();
    const [toast, setToast] = useState<{ message: string; color?: string, persistent?: boolean; } | null>(null);
    const selectedPersonnel = useSelector((state: IRootState) => state.personnel.selectedPersonnel);
    const [yearlyRates, setYearlyRates] = useState<Array<BenefitRate>>();
    const [benefitYears, setBenefitYears] = useState<Array<number>>();
    const [isLoading, setIsLoading] = useState<boolean>();
    const [benefitRateProp, setBenefitRateProp] = useState<BenefitRate>(); // prop data to send to dialog

    const dispatch = useDispatch();

    // Initial render
    useEffect(() => {
        onCreate();

        return () => {
            onDestroy();
        };
    }, []);

    useEffect(() => {
        initializeData();
    }, [selectedPersonnel]);

    const onCreate = () => {
        console.log('EmployeeBenefits onCreate()');
        initializeData();
    };

    const onDestroy = () => {
        console.log('EmployeeBenefits onDestroy()');
    };

    const showToast = (message: string, color?: string, persistent?: boolean) => {
        setToast({ message, persistent, color });
    };

    const displayTitle = () => {
        const title = 'Rate Table';
        return title;
    };

    const initializeData = async () => {
        await getBenefitYears();
        getYearlyRates();
    };

    // gets benefit years and maps them to dropdown
    const getBenefitYears = async () => {
        setIsLoading(true);
        let results = await get(`/v1.0/landmen/benefits/get-benefit-years`);

        if (response.ok && results) {
            setBenefitYears(results);
            setValue('year', results[0]);
        } else {
            showToast('Unable to fetch benefit years', `rgba(255,0,0,0.5)`);
        }
    };

    const benefitYearsDropdown = () => {
        return benefitYears
            ? benefitYears?.map((ct: number) => {
                return { value: ct, label: ct.toString() };
            })
            : [];
    };

    const getYearlyRates = async () => {
        let year = getValues()?.year;
        setIsLoading(true);
        setYearlyRates(null);

        let results = await get(`/v1.0/landmen/benefits/${year}/get-yearly-rates`);

        if (response.ok && results.length > 0) {
            setIsLoading(false);
            setYearlyRates(results);
        } else if (results.length === 0) {
            showToast(`No rates found for ${year}`, `rgba(255,0,0,0.5)`);
        } else {
            console.log(response);
            showToast(`Unable to fetch yearly rates: ${response}`, `rgba(255,0,0,0.5)`);
        }

        setIsLoading(false);
    };

    /**
     * Sets the selected Personnel to the store if the given ID is valid and it's not already the same personnel
     * @param personnel - personnel payload
     */
    const setSelectedPersonnel = (personnel: TSDILandman) => {
        if (personnel) {
            personnel?.lmNo !== selectedPersonnel?.lmNo
                ? dispatch({ type: '[personnel] SET_SELECTED_PERSONNEL', payload: personnel })
                : showToast('Selected personnel already set', `rgba(78,117,154,0.5)`);

        } else {
            showToast('Selected personnel is undefined', `rgba(255,0,0,0.5)`);
        }
    };

    const toggleAdvancedQuickFind = () => {
        dispatch(actions.layout.showDialog(DialogList.PERSONNEL_ADVANCED_QUICK_FIND));
    };

    const toggleEditRate = (row: BenefitRate) => {
        setBenefitRateProp({ ...row }); // selected row information
        dispatch(actions.layout.showDialog(DialogList.EDIT_BENEFIT_RATE));
    };

    const hideDialog = () => {
        dispatch(actions.layout.hideDialog());
    };
    // end dialogs

    // adds new benefit year & copies previous year's data
    const addNewBenefitRateYear = async () => {
        await post(`/v1.0/landmen/benefits/${benefitYears[0] + 1}/add-benefit-rate-year`);

        if (response.ok) {
            showToast('Benefit year rates successfully created');
            initializeData();
        } else {
            showToast('Unable to create new benefit rates: ' + response, `rgba(255,0,0,0.5)`);
        }
    };

    return (
        <div>
            <PersonnelNavBar />
            <div>
                {toast && (
                    <PersonnelToast
                        message={toast.message}
                        color={toast.color}
                        onClose={() => setToast(null)}
                        persistent={toast.persistent}
                    />
                )}
            </div>
            <PersonnelSearch toggleAdvancedQuickFind={toggleAdvancedQuickFind} />
            <Page>
                <Page.Section width={12}>
                    <Panel backgroundColor="#D3D3D3">
                        <Panel panelTitle={displayTitle()}>
                            <div className="form-row">
                                <RHFSelect
                                    name="year"
                                    label="Year"
                                    labelOnLeft={true}
                                    options={benefitYearsDropdown()}
                                    register={register}
                                    onChange={() => getYearlyRates()}
                                />
                                <Button
                                    text="Create Yearly Rates / Copy Previous Year"
                                    onClick={() => addNewBenefitRateYear()}
                                    intent="primary"
                                    style={{ width: 'auto' }}
                                />
                            </div>
                            <hr />
                            {isLoading && (
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        height: '50vh',
                                        width: '100%',
                                    }}
                                >
                                    <LoadingSpinner />
                                </div>
                            )}
                            <br />
                            {yearlyRates?.length > 0 && <div style={{ overflowY: 'scroll', maxHeight: '55rem' }}>
                                <table style={{ width: '100%' }}>
                                    <thead>
                                        <tr style={{ background: '#d6d6d6' }}>
                                            <th style={{ width: '5%' }} className="sticky-header">Type</th>
                                            <th style={{ width: '9%' }} className="sticky-header">Category</th>
                                            <th style={{ width: '5%' }} className="sticky-header">Code</th>
                                            <th style={{ width: '15%' }} className="sticky-header">Code Desc</th>
                                            <th style={{ width: '10%' }} className="sticky-header">Monthly Total</th>
                                            <th style={{ width: '10%' }} className="sticky-header">Monthly Company Total</th>
                                            <th style={{ width: '10%' }} className="sticky-header">Annual Total</th>
                                            <th style={{ width: '10%' }} className="sticky-header">Annual Company Total</th>
                                            <th style={{ width: '15%' }} className="sticky-header">Amount / Percentage</th>
                                            <th style={{ width: '10%' }} className="sticky-header">Actions</th>
                                        </tr>
                                    </thead>
                                    {yearlyRates?.map((rate: BenefitRate, index: number) => {
                                        return (
                                            <tbody key={index}>
                                                <tr>
                                                    <td style={{ width: '5%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                        {rate.benefitType}
                                                    </td>
                                                    <td style={{ width: '9%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                        {rate.category}
                                                    </td>
                                                    <td style={{ width: '5%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                        {rate.code}
                                                    </td>
                                                    <td style={{ width: '15%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                        {rate.description}
                                                    </td>
                                                    <td style={{ width: '10%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                        {new Intl.NumberFormat('en-US', {
                                                            style: 'currency',
                                                            currency: 'USD',
                                                        }).format(rate?.totalMonthAmount)}
                                                    </td>
                                                    <td style={{ width: '10%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                        {rate?.compensationType === 'A'
                                                            ? new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(rate?.companyMonthAmount)
                                                            : new Intl.NumberFormat('en-US', {
                                                                style: 'percent',
                                                                minimumFractionDigits: 2,
                                                            }).format(rate?.companyMonthAmount / 100)
                                                        }
                                                    </td>
                                                    <td style={{ width: '10%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                        {new Intl.NumberFormat('en-US', {
                                                            style: 'currency',
                                                            currency: 'USD',
                                                        }).format(rate?.totalAnnualAmount)}
                                                    </td>
                                                    <td style={{ width: '10%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                        {rate?.compensationType === 'A'
                                                            ? new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(rate?.companyAnnualAmount)
                                                            : new Intl.NumberFormat('en-US', {
                                                                style: 'percent',
                                                                minimumFractionDigits: 2,
                                                            }).format(rate?.companyAnnualAmount / 100)
                                                        }
                                                    </td>
                                                    <td style={{ width: '15%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                        {rate.compensationType === 'P' ? 'Percentage' : 'Amount'}
                                                    </td>
                                                    <td style={{ width: '18%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                        {/* <a onClick={() => toggleEditRate(rate)}> */}
                                                        <Button
                                                            text={'Edit'}
                                                            onClick={() => toggleEditRate(rate)}
                                                            style={{ width: '100%' }}
                                                        />
                                                        {/* </a> */}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        );
                                    })}
                                </table>
                            </div>}
                        </Panel>
                    </Panel>
                </Page.Section>
            </Page>
            <EditBenefitRateDialog
                dialogName={DialogList.EDIT_BENEFIT_RATE}
                hideDialog={hideDialog}
                showToast={showToast}
                updateBenefit={initializeData}
                benefitRate={benefitRateProp}
            />
            <PersonnelAdvancedQuickFind
                dialogName={DialogList.PERSONNEL_ADVANCED_QUICK_FIND}
                hideDialog={hideDialog}
                selectedPersonnel={selectedPersonnel}
                selectPersonnel={setSelectedPersonnel}
            />
        </div>
    );
};

export default BenefitRatesPage;


// WEBPACK FOOTER //
// ./src/components/personnel/pages/BenefitRatesPage.tsx